"use client";

import { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { Compass, CornerDownLeft, ArrowUp, ArrowDown } from "lucide-react";
import { clsx } from "clsx";
import { useAuth, UserRole } from "@/lib/AuthContext";
import styles from "./NavigationBot.module.css";

type RouteDef = {
  label: string;
  path: string;
  keywords: string[];
  role?: UserRole | "public" | "auth" | "authenticated";
};

const PUBLIC_ROUTES: RouteDef[] = [
  {
    label: "Home",
    path: "/",
    keywords: ["home", "overview", "main", "start", "summary"],
  },
  { label: "About", path: "/about", keywords: ["about", "info", "company"] },
  {
    label: "Pricing",
    path: "/pricing",
    keywords: ["pricing", "plans", "price", "subscribe"],
  },
  { label: "FAQ", path: "/faq", keywords: ["faq", "questions", "answers"] },
  {
    label: "Contact",
    path: "/contact",
    keywords: ["contact", "email", "reach", "message"],
  },
  { label: "Help", path: "/help", keywords: ["help", "support", "contact us"] },
  {
    label: "Terms",
    path: "/terms",
    keywords: ["terms", "legal", "conditions"],
  },
  {
    label: "Privacy",
    path: "/privacy",
    keywords: ["privacy", "policy", "data"],
  },
  {
    label: "Events",
    path: "/events",
    keywords: ["events", "calendar", "activities"],
  },
  {
    label: "Courses",
    path: "/courses",
    keywords: ["courses", "catalog", "subjects"],
  },
  {
    label: "Departments",
    path: "/departments",
    keywords: ["departments", "faculty", "schools"],
  },
  {
    label: "Accessibility",
    path: "/accessibility",
    keywords: ["accessibility", "a11y"],
  },
];

const AUTH_ROUTES: RouteDef[] = [
  {
    label: "Register",
    path: "/auth/signup",
    keywords: ["signup", "register", "join", "create account"],
    role: "auth",
  },
  {
    label: "Log In",
    path: "/auth/login",
    keywords: ["login", "sign in", "authenticate"],
    role: "auth",
  },
];

const AUTHENTICATED_ROUTES: RouteDef[] = [
  {
    label: "Dashboard",
    path: "/dashboard",
    keywords: ["home", "overview", "main", "start", "summary"],
    role: "authenticated",
  },
  {
    label: "Ask AI",
    path: "/ask",
    keywords: ["question", "chat", "ai", "ask", "query", "answer"],
    role: "authenticated",
  },
  {
    label: "Learn",
    path: "/learn",
    keywords: ["study", "courses", "learn", "materials", "lecture"],
    role: "authenticated",
  },
  {
    label: "Notes",
    path: "/tools/notes",
    keywords: ["note", "notes", "summary", "writeup"],
    role: "authenticated",
  },
  {
    label: "Likely Questions",
    path: "/likely-questions",
    keywords: [
      "likely questions",
      "question library",
      "past paper",
      "predicted questions",
      "probable questions",
    ],
    role: "authenticated",
  },
  {
    label: "Flashcards",
    path: "/tools/flashcards",
    keywords: ["flashcard", "memorize", "cards", "recall"],
    role: "authenticated",
  },
  {
    label: "Quizzes",
    path: "/tools/quiz-builder",
    keywords: ["quiz", "test", "exam practice", "practice"],
    role: "authenticated",
  },
  {
    label: "Formula Sheet",
    path: "/tools/formula-sheet",
    keywords: ["formula", "equations", "math", "symbols"],
    role: "authenticated",
  },
  {
    label: "Study Timer",
    path: "/tools/study-timer",
    keywords: ["timer", "pomodoro", "focus", "clock"],
    role: "authenticated",
  },
  {
    label: "Whiteboard",
    path: "/tools/whiteboard",
    keywords: ["whiteboard", "draw", "canvas", "sketch"],
    role: "authenticated",
  },
  {
    label: "Grade Calculator",
    path: "/tools/gpa-calculator",
    keywords: ["grade", "cgpa", "gpa", "calculator", "result"],
    role: "authenticated",
  },
  {
    label: "Calendar",
    path: "/calendar",
    keywords: ["calendar", "schedule", "events", "dates", "timetable"],
    role: "authenticated",
  },
  {
    label: "Profile",
    path: "/profile",
    keywords: ["profile", "account", "me", "my info"],
    role: "authenticated",
  },
  {
    label: "Settings",
    path: "/settings",
    keywords: ["settings", "preferences", "config", "options"],
    role: "authenticated",
  },
  {
    label: "Notifications",
    path: "/notifications/history",
    keywords: ["notification", "alerts", "bell", "updates"],
    role: "authenticated",
  },
  {
    label: "Saved",
    path: "/user/saved",
    keywords: ["saved", "bookmarks", "favorites", "pins"],
    role: "authenticated",
  },
  {
    label: "Top Up",
    path: "/pay",
    keywords: ["pay", "credits", "top up", "upgrade", "billing", "plan"],
    role: "authenticated",
  },
  {
    label: "Leaderboard",
    path: "/leaderboard",
    keywords: ["leaderboard", "rank", "top students"],
    role: "authenticated",
  },
  {
    label: "Badges",
    path: "/badges",
    keywords: ["badges", "achievements", "rewards"],
    role: "authenticated",
  },
  {
    label: "My Progress",
    path: "/my-progress",
    keywords: ["progress", "grades", "performance"],
    role: "authenticated",
  },
  {
    label: "Changelog",
    path: "/changelog",
    keywords: ["changelog", "updates", "new features"],
    role: "authenticated",
  },
  {
    label: "Community",
    path: "/community",
    keywords: ["community", "forum", "discussions"],
    role: "authenticated",
  },
];

const ADMIN_ROUTES: RouteDef[] = [
  {
    label: "Admin",
    path: "/admin",
    keywords: ["admin", "manage", "control"],
    role: "admin",
  },
  {
    label: "Admin Reps",
    path: "/admin/reps",
    keywords: ["reps", "multiplier", "course rep multiplier"],
    role: "admin",
  },
  {
    label: "Admin Feedback",
    path: "/admin/feedback",
    keywords: ["feedback", "ratings", "answer quality"],
    role: "admin",
  },
  {
    label: "Admin Analytics",
    path: "/admin/analytics",
    keywords: ["analytics", "usage", "revenue", "charts"],
    role: "admin",
  },
  {
    label: "Admin Logs",
    path: "/admin/logs",
    keywords: ["logs", "errors", "system logs"],
    role: "admin",
  },
  {
    label: "Admin Config",
    path: "/admin/config",
    keywords: ["config", "settings", "feature flags", "pricing"],
    role: "admin",
  },
  {
    label: "Payments",
    path: "/admin/transactions",
    keywords: ["payments", "transactions", "revenue", "ledger"],
    role: "admin",
  },
];

const COURSE_REP_ROUTES: RouteDef[] = [
  {
    label: "Course Rep Hub",
    path: "/course-rep",
    keywords: ["course rep", "rep", "students", "class"],
    role: "course_rep",
  },
];

const ROUTES: RouteDef[] = [
  ...PUBLIC_ROUTES,
  ...AUTH_ROUTES,
  ...AUTHENTICATED_ROUTES,
  ...ADMIN_ROUTES,
  ...COURSE_REP_ROUTES,
];

export default function NavigationBot({
  collapsed = false,
  variant = "sidebar",
}: {
  collapsed?: boolean;
  variant?: "sidebar" | "mobile";
}) {
  const router = useRouter();
  const { role } = useAuth();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list: RouteDef[] = [];

    if (!role) {
      // Unauthenticated: public routes + auth routes only
      list = ROUTES.filter(
        (r) => r.role === "public" || r.role === "auth" || !r.role,
      );
    } else if (role === "admin") {
      // Admin: all routes
      list = ROUTES;
    } else if (role === "course_rep") {
      // Course rep: all routes except admin-only routes
      list = ROUTES.filter((r) => r.role !== "admin");
    } else {
      // Default authenticated (student): public + auth + authenticated routes
      list = ROUTES.filter(
        (r) =>
          !r.role ||
          r.role === "public" ||
          r.role === "auth" ||
          r.role === "authenticated",
      );
    }

    if (!q) return list.slice(0, 7);
    return list
      .map((r) => {
        const hay = (r.label + " " + r.keywords.join(" ")).toLowerCase();
        let score = 0;
        if (r.label.toLowerCase().includes(q)) score += 6;
        r.keywords.forEach((k) => {
          if (k.includes(q)) score += 3;
          if (q.includes(k) && k.length > 2) score += 2;
        });
        if (hay.includes(q)) score += 1;
        return { r, score };
      })
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 8)
      .map((x) => x.r);
  }, [query, role]);

  const go = (path: string) => {
    setOpen(false);
    setQuery("");
    router.push(path);
  };

  const onInputKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (results[active]) go(results[active].path);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const trigger =
    variant === "mobile" ? (
      <button
        onClick={() => setOpen(true)}
        className={`flex-1 ${styles.navBotMobileTrigger}`}
      >
        <div className="flex flex-col items-center gap-1">
          <div
            className={clsx(
              "p-2 rounded-xl transition-all duration-300",
              open ? "bg-y text-black scale-110" : "text-y",
            )}
          >
            <Compass size={20} />
          </div>
          <span className="text-xs font-bold uppercase tracking-wider text-y">
            Go
          </span>
        </div>
      </button>
    ) : (
      <button
        onClick={() => setOpen(true)}
        aria-label="Open navigation (Command or Control + K)"
        className={clsx(
          "group relative w-full flex items-center gap-3 p-3 rounded-xl hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors mb-1",
          collapsed && "justify-center",
        )}
      >
        <span className="relative inline-flex">
          <Compass size={20} />
          {collapsed && (
            <kbd className="absolute -right-1.5 -top-1.5 flex h-3.5 w-3.5 items-center justify-center rounded-[3px] border border-border bg-background text-xs font-black text-y">
              K
            </kbd>
          )}
        </span>
        {!collapsed && (
          <>
            <span className="text-xs font-bold uppercase tracking-widest whitespace-nowrap">
              Navigate
            </span>
            <kbd className="ml-auto text-xs font-black text-muted-foreground border border-border rounded px-1.5 py-0.5">
              ⌘K
            </kbd>
          </>
        )}
        {collapsed && (
          <span className="pointer-events-none absolute left-full z-[200] ml-3 whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-xs font-bold text-background opacity-0 transition-opacity group-hover:opacity-100">
            Navigate · ⌘K
          </span>
        )}
      </button>
    );

  return (
    <>
      {trigger}
      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {open && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[200] flex items-start justify-center pt-[12vh] px-4"
                onClick={() => setOpen(false)}
              >
                <motion.div
                  initial={{ opacity: 0, y: -16, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -16, scale: 0.98 }}
                  transition={{ type: "spring", bounce: 0.2, duration: 0.3 }}
                  className="w-full max-w-xl bg-background border-2 border-y/30 rounded-2xl shadow-2xl shadow-black/30 ring-1 ring-y/10 overflow-hidden"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center gap-3 p-4 border-b border-border">
                    <Compass className="w-5 h-5 text-y" />
                    <input
                      ref={inputRef}
                      value={query}
                      onChange={(e) => {
                        setQuery(e.target.value);
                        setActive(0);
                      }}
                      onKeyDown={onInputKey}
                      placeholder="Tell me where to go — e.g. “open my notes” or “show the leaderboard”"
                      className="flex-1 bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground"
                    />
                    <kbd className="text-xs font-black text-muted-foreground border border-border rounded px-1.5 py-0.5 hidden sm:block">
                      ESC
                    </kbd>
                  </div>

                  <div
                    className={`max-h-[50vh] overflow-y-auto p-2 ${styles.navBotPanel}`}
                  >
                    {results.length === 0 ? (
                      <p className="text-center text-xs text-muted-foreground py-8">
                        No destination matches “{query}”.
                      </p>
                    ) : (
                      results.map((r, i) => (
                        <button
                          key={r.path}
                          onMouseEnter={() => setActive(i)}
                          onClick={() => go(r.path)}
                          className={clsx(
                            "w-full flex items-center justify-between gap-3 px-3 py-3 rounded-xl transition-colors text-left",
                            i === active ? "bg-y/10" : "hover:bg-secondary/60",
                          )}
                        >
                          <span className="text-sm font-bold text-foreground">
                            {r.label}
                          </span>
                          <span className="flex items-center gap-2">
                            <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                              {r.path}
                            </span>
                            {i === active && (
                              <CornerDownLeft className="w-3.5 h-3.5 text-y" />
                            )}
                          </span>
                        </button>
                      ))
                    )}
                  </div>

                  <div className="flex items-center gap-4 px-4 py-2.5 border-t border-border text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <ArrowUp className="w-3 h-3" />
                      <ArrowDown className="w-3 h-3" />
                      Navigate
                    </span>
                    <span className="flex items-center gap-1">
                      <CornerDownLeft className="w-3 h-3" />
                      Go
                    </span>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body,
        )}
    </>
  );
}
